<script setup>
/* Imports */
import {
  ref,
  computed,
} from 'vue';
import { useRoute } from 'vue-router';
import Button from '@garmin/components-global-vue-button';
import { useProductOptions } from '@garmin/ui-product-ems-filters';
import {
  mapGetters,
  mapActions,
  request,
  convertCartConversionQueryParamsToString,
  announceAccessibilityMessage,
  getCookie,
} from '../helpers/mainHelpers.js';
import {
  sendAnalyticsEvent,
  sanitizeAnalyticsString,
  getSelectedEmsOption,
  getSelectedEmsOptionIndex,
  getAnalyticsPriceData,
  getCategory3Data,
} from '../helpers/ga/analytics.js';

/* Store - Getters */
const {
  getShowAddToCartBtn: showAddToCartBtn,
  getPvName: pvName,
  getProductPageApi: productPageApi,
  getGalleryImageList: galleryImageList,
  getOptionListByType: optionListByType,
  getOptionsList: optionsList,
  getGlobalProductName: globalProductName,
  getAddToCartLoading: addToCartLoading,
  getProductBySku,
  getTranslations: translations,
} = mapGetters();

/* Props */
const props = defineProps({
  productId: {
    type: String,
    default: '',
  },
  partNumber: {
    type: String,
    default: '',
  },
  productVariation: {
    type: String,
    default: '',
  },
  showStickyAddToCartBtn: {
    type: Boolean,
    default: false,
  },
  addToCartHref: {
    type: String,
    default: '',
  },
  addToCartText: {
    type: String,
    default: '',
  },
});

/* Composables */
import { useGenericAnalyticsDataHelper } from '../composables/useAnalyticsData.js';

/* Inject composables */
const { genericAnalyticsData } = useGenericAnalyticsDataHelper();
const productOptions = useProductOptions();

/* Route */
const route = useRoute();

/* State */
const state = ref({
  cartCount: null,
});

const {
  setInterstitialIsVisible,
  setInterstitialProductDetails,
  setAddToCartLoadingState,
  callInterstitialAccessories,
} = mapActions();

/* Computed */
const getCategory3Value = computed(() => {
  const productName = sanitizeAnalyticsString(globalProductName.value) ?? 'NA';
  return getCategory3Data(productName);
});

const computedAddToCartHref = computed(() => {
  const stringifiedCartConversionQueryParams = convertCartConversionQueryParamsToString(route.query);
  return stringifiedCartConversionQueryParams ? `${props.addToCartHref}${stringifiedCartConversionQueryParams}` : props.addToCartHref;
});

const computedOptionsList = computed(() => {
  if (window?.envSettings?.ITFE_PRODUCT_PAGE_ENABLE_REUSABLE_EMS_UI) {
    return productOptions.optionsList.value;
  }
  return optionsList.value;
});

const analyticsIndex = computed(() => {
  if (window?.envSettings?.ITFE_PRODUCT_PAGE_ENABLE_REUSABLE_EMS_UI) {
    return getSelectedEmsOptionIndex(productOptions.getOptionListByType('image'));
  }
  return getSelectedEmsOptionIndex(optionListByType.value('image'));
});

const buttonClass = computed(() => ({
  app__product__cta__button: true,
  app__product__cta__loading: addToCartLoading.value,
}));

/* Methods */
const getCart = async () => request({
  url: `${productPageApi.value}/cart`,
  method: 'POST',
});

const addToCart = async (sku) => request({
  url: `${productPageApi.value}/addToCart/${sku}`,
  method: 'POST',
});

const updateCartCount = async () => {
  const event = document.createEvent('Event');
  event.initEvent('cartCountChanged', true, false);
  document.dispatchEvent(event);
};

const handleAddToCart = async (event) => {
  const url = event.target.href;

  event.preventDefault();

  try {
    setAddToCartLoadingState(true);

    // Make sure that we have a valid cart
    await getCart();
    // Add the bundle to cart
    await addToCart(props.partNumber);
    // Trigger update for the cart count in the header
    updateCartCount();

    // Fetch compatible accessories to display in the interstitial
    await callInterstitialAccessories();
    // Show interstitial window
    setInterstitialIsVisible(true);
    setInterstitialProductDetails({
      pvName: pvName.value,
      imageSrc: galleryImageList.value?.length && galleryImageList.value?.[0]?.imageSrc,
      altText: galleryImageList.value?.length && galleryImageList.value?.[0]?.altText,
    });

    setAddToCartLoadingState(false);

    state.value.cartCount = getCookie('GarminCartCount');

    if (state.value?.cartCount) {
      // Accessibility announcement
      announceAccessibilityMessage(translations.value?.ITFE_PRODUCT_PAGES_ATC_CART_COUNT_MESSAGE?.replace('{{count}}', state.value.cartCount));
    }
  } catch {
    // If we have an error, redirect to the cart
    window.location.assign(url);
  }
};

const addToCartUTagEvent = async () => {
  const productData = getProductBySku.value(props.partNumber);
  const productPrice = {
    priceObj: {
      listPrice: productData?.price?.listPrice,
      salePrice: productData?.price?.salePrice,
    },
    sellable: productData?.isSellable,
    showPrice: productData?.showPrice,
  };

  const analyticsData = {
    // Send these as undefined since we're going to work in another context to update these values
    ...genericAnalyticsData(
      undefined,
      undefined,
    ),
    index: analyticsIndex.value,
    product_id: [props.productId || 'NA'],
    product_brand: window?.utag_data?.product_brand[0] || 'NA',
    product_category: window?.utag_data?.product_category || 'NA',
    product_category2: window?.utag_data?.product_category2 || 'NA',
    product_category3: getCategory3Value.value,
    product_category4: getSelectedEmsOption(computedOptionsList.value, 0),
    product_category5: getSelectedEmsOption(computedOptionsList.value, 1),
    product_variant: [sanitizeAnalyticsString([props.productVariation || 'NA'])],
    product_name: [sanitizeAnalyticsString(globalProductName.value) || 'NA'],
    product_original_price: getAnalyticsPriceData(productPrice).listPrice,
    product_price: getAnalyticsPriceData(productPrice).price,
    product_sku: props.partNumber || 'NA',
    product_url: window?.location?.href?.split('?')?.[0] || 'NA',
    product_availability: getAnalyticsPriceData(productPrice)?.availability,
    product_quantity: '1',
    item_family: [getCategory3Value.value?.[0] || 'NA'],
    is_sale: getAnalyticsPriceData(productPrice)?.isSale,
    in_stock: getAnalyticsPriceData(productPrice)?.inStock,
    price_shown: getAnalyticsPriceData(productPrice)?.priceShown,
    link_type: props.showStickyAddToCartBtn ? 'sticky button' : 'button',
    // Send the following fields as 'NA' for now since they are not available in the current context
    creative_name: 'NA',
    creative_slot: 'NA',
    location_id: 'NA',
    promotion_name: 'NA',
    promotion_id: 'NA',
    bundle_id: 'NA',
    is_bundle: 'NA',
    sale_type: 'NA',
    tealium_event: 'add_to_cart',
    event_label: 'Add to Cart',
  };

  sendAnalyticsEvent(analyticsData);
};

const addToCartBtnClick = (event) => {
  handleAddToCart(event);
  addToCartUTagEvent();
};
</script>

<template>
  <Button
    v-if="showAddToCartBtn"
    id="js__cta__buy"
    data-testid="add-to-cart-button"
    :href="computedAddToCartHref"
    :class="buttonClass"
    size="large"
    theme="candy-blue"
    @click="addToCartBtnClick($event)"
  >
    {{ props.addToCartText }}
  </Button>
</template>

<style lang="scss" scoped>
.app__product__cta__button {
  width: 100%;
  margin: 0 0.5rem 0.5rem 0;

  @include productBreakpoint('mobile-m') {
    text-align: center;
    max-width: none;
    width: 100%;
  }

  &__loading {
    cursor: wait;

    &::after {
      content: '';
      display: block;
      background-color: rgba($color-white, 0.8);
      width: 100%;
      height: 100%;
      position: absolute;
      left: 0;
      top: 0;
      cursor: wait;
    }
  }

  a {
    width: 100%;
  }
}
</style>
